import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { navItems } from "@/mocks/site";

function useScrolled(threshold = 20) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

export default function Header() {
  const scrolled = useScrolled();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setMenuOpen(false);
    navigate(q ? `/news?q=${encodeURIComponent(q)}` : "/news");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-background-50/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-lg font-heading font-bold">
            佳
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-base md:text-lg font-bold text-foreground-950 tracking-wide">
              森泽佳奈
            </span>
            <span className="text-[10px] md:text-xs text-foreground-500 tracking-[0.2em] uppercase">
              Official Site
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-primary-600 font-medium bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden lg:block">
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-3 py-1.5 focus-within:border-primary-300 transition-colors">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索资讯或作品"
                className="bg-transparent outline-none text-sm text-foreground-800 placeholder:text-foreground-400 w-40"
              />
            </div>
          </form>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-up">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-3 py-2 focus-within:border-primary-300">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索资讯或作品"
                className="bg-transparent outline-none text-sm text-foreground-800 placeholder:text-foreground-400 w-full"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm rounded-md transition-colors ${
                    isActive
                      ? "text-primary-600 font-medium bg-primary-50"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}